import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useI18n } from '@whizz/react-i18n';
import { Lesson } from '@whizz/api-model';
import type { HTMLAttributes } from 'react';
import type { Row } from 'react-table';
import type { AllColorType } from '../../util/types';
import type { CustomColumnProps, CustomRowProps } from '../../components/table/types';
import type { StudentType } from '../../pages/types';
import { Button, ButtonIcon, Table } from '../../components';
import { useModal } from '../../hooks/useModal';
import { DEFAULT_TRANSITION_DURATION, DEFAULT_TRANSITION_FUNC, getPolishedColors } from '../../util/styled';
import { GLOBAL_ICONS } from '../../util/vars';
import { DateTime } from 'luxon';
import StudentsPassword from '../span/StudentsPassword';
import { format } from 'd3-format';

export type StudentsTableAccessors =
  | 'name'
  | 'maths_age'
  | 'daysSinceLastLogon'
  | 'login_details'
  | 'lesson_details'
  | 'haveNotCompletedRelevantDateString'
  | 'progress'
  | 'progressions'
  | 'minutes';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
interface Props extends HTMLProps {
  /**
   * Table identification.
   */
  id: string;
  /**
   * Students list data.
   */
  data: StudentType[];
  /**
   * Second column to display along name.
   */
  column?: StudentsTableAccessors | StudentsTableAccessors[] | null;
  /**
   * Styles table with color.
   */
  color?: AllColorType;
  /**
   * Adds border to table container.
   */
  withBorder?: boolean;
  /**
   * Adds extra message at footer.
   */
  headerMessage?: React.ReactNode | string;
  /**
   * Adds extra message at footer.
   */
  footerMessage?: React.ReactNode | string;
  /**
   * The specific subRow state key to use
   */
  state?: string
  /**
   * The default column to sort by
   */
  sortBy?: {
    column: StudentsTableAccessors;
    desc: boolean;
  }
  onStudentClick?: (id: number) => void;
}

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_maths_age'],
    [data-column='cell_maths_age'],
    [data-column='cell_login_details'],
    [data-column='cell_lesson_details'],
    [data-id='col_haveNotCompletedRelevantDate'],
    [data-column='cell_haveNotCompletedRelevantDateString'],
    [data-id='col_expander'],
    [data-column='cell_progress'],
    [data-column='cell_progressions'],
    [data-column='cell_minutes'] {
      text-align: right;
    }
    [data-id='col_maths_age'],
    [data-id='col_login_details'],
    [data-id='col_lesson_details'],
    [data-id='col_haveNotCompletedRelevantDateString'],
    [data-id='col_expander'],
    [data-id='col_progress'],
    [data-id='col_progressions'],
    [data-id='col_minutes'] {      
      div {
        justify-content: end !important;
      }      
    }
    [data-column='cell_name'] > div {
      transform: translate(0);
      transition: transform ${DEFAULT_TRANSITION_FUNC} ${DEFAULT_TRANSITION_DURATION};
      
      &:before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: -15px;
        background: black;
        border-radius: 100%;
        margin: auto;
        width: 5px;
        height: 5px;
        transform: scale(0);
        transition: transform ${DEFAULT_TRANSITION_FUNC} ${DEFAULT_TRANSITION_DURATION};
      }
    }
    [data-expanded='true'] [data-column='cell_name'] > div {
      position: relative;
      text-decoration: underline;
      transform: translate(20px, 0);
      
      &:before {        
        transform: scale(1);
      }
    }     
  }
`;

const StudentsTable = React.forwardRef<Ref, Props>(({
  id,
  data,
  column = 'maths_age',
  color = 'primary',
  withBorder = false,
  sortBy = {
    column: 'name',
    desc: false,
  },
  state,
  headerMessage,
  footerMessage,
  onStudentClick,
  ...other
}: Props, ref): JSX.Element => {

  const { translate, locale } = useI18n();

  const [ modal, toggle ] = useModal(() => (
    <>
      <p>{translate('components.TABLES.LESSON_DETAILS.NOT_SUPPORTED.LINE_1')}</p>
      <p>{translate('components.TABLES.LESSON_DETAILS.NOT_SUPPORTED.LINE_2')}</p>
    </>
  ), {
    title: translate('components.TABLES.LESSON_DETAILS.NOT_SUPPORTED.TITLE'),
  });

  const columns = [
    {
      id: 'name',
      Header: translate('components.TABLES.STUDENTS.NAME'),
      accessor: 'name',
      Cell: ({ row }: { row: any }) => {
        const label = row.values.name;
        return onStudentClick && typeof label === 'string' ? (<Button asLink onClick={() => onStudentClick(parseInt(row.original.id))}>{label}</Button>) : label;
      },
    },
    {
      id: 'maths_age',
      Header: translate('components.TABLES.STUDENTS.MATHS_AGE'),
      accessor: 'maths_age',
    },
    {
      id: 'daysSinceLastLogon',
      Header: translate('components.TABLES.STUDENTS.LAST_LOGGED_IN'),
      accessor: 'daysSinceLastLogon',
    },
    {
      id: 'login_details',
      Header: translate('components.TABLES.STUDENTS.LOGIN_DETAILS'),
      Cell: ({ row }: { row: Row }) =>
        row.canExpand ? (
          <ButtonIcon
            icon={GLOBAL_ICONS[row.isExpanded ? 'see_off' : 'see_on']}
            size={'sm'}
            color={'transparent'}
            {...row.getToggleRowExpandedProps({})}
          />
        ) : null,
      accessor: 'login_details',
      disableSortBy: true,
    },
    {
      id: 'lesson_details',
      Header: translate('components.TABLES.STUDENTS.LESSON_DETAILS'),
      Cell: ({ row }: { row: Row }) =>
        row.canExpand ? (
          <ButtonIcon
            icon={GLOBAL_ICONS[row.isExpanded ? 'see_off' : 'see_on']}
            size={'sm'}
            color={'transparent'}
            {...row.getToggleRowExpandedProps({})}
          />
        ) : null,
      accessor: 'lesson_details',
      disableSortBy: true,
    },
    {
      id: 'progress',
      Header: translate('components.TABLES.STUDENTS.PROGRESS'),
      accessor: 'progress',
    },
    {
      id: 'haveNotCompletedRelevantDateString',
      Header: translate('components.TABLES.STUDENTS.ASSESSMENT_STARTED_DATE'),
      accessor: 'haveNotCompletedRelevantDateString',
      sortType: (rowA: { original: { haveNotCompletedRelevantDate: DateTime } }, rowB: { original: { haveNotCompletedRelevantDate: DateTime } }): number => {
        return rowA.original.haveNotCompletedRelevantDate === rowB.original.haveNotCompletedRelevantDate ? 0 :
          (rowA.original.haveNotCompletedRelevantDate > rowB.original.haveNotCompletedRelevantDate) ? -1 : 1;
      },
    },
    {
      id: 'progressions',
      Header: translate('components.TABLES.STUDENTS.PROGRESSIONS'),
      accessor: 'progressions',
    },
    {
      id: 'minutes',
      Header: translate('components.TABLES.STUDENTS.MINUTES'),
      accessor: 'minutes',
    },
  ] as CustomColumnProps<StudentsTableAccessors>[];

  const getColumnsToShow = () => {
    const accessorsToCheck: string[] = [ 'name' ];

    if (Array.isArray(column)) {
      accessorsToCheck.push(...column);
    } else {
      accessorsToCheck.push(column!);
    }

    return columns.filter((col) => accessorsToCheck.includes(col.id!));
  };

  const playLesson = (lessonId: Lesson['id']) => {
    //Todo: Rather than creating a new `Lesson` instance, this should be obtained from api so it has the correct locale etc.
    const lesson = new Lesson({ id: lessonId });
    return () => window.open(lesson.player_url);
  };

  const showNotSupportedModal = () => {
    toggle(true);
  };

  const getFormattedData: (subRowKey?: string) => CustomRowProps<StudentsTableAccessors>[] = (subRowKey) => {
    const arr: CustomRowProps<StudentsTableAccessors>[] = [];
    data.map((student) => {
      arr.push({
        id: student.id.toString(),
        name: student.fullName,
        maths_age: format('.2f')((student.maths_age) ? student.maths_age : 0),
        login_details: null,
        progress: `${student.progress}`,
        haveNotCompletedRelevantDateString: DateTime.fromISO(`${student.haveNotCompletedRelevantDate}`).toLocaleString(
          undefined, locale ? { locale: locale.getId() } : undefined,
        ),
        daysSinceLastLogon: DateTime.isDateTime(student.lastLogonDate) ? translate('components.TABLES.LAST_LOGGED_IN', { count: (Math.floor(0 - student.lastLogonDate.startOf('day').diffNow().as('days'))) }) : '-',
        haveNotCompletedRelevantDate: student.haveNotCompletedRelevantDate,
        progressions: format('.1f')((student.chartsData?.progression) ? student.chartsData.progression : 0),
        minutes: format('.0f')((student.chartsData?.qualityLearningTime) ? student.chartsData.qualityLearningTime : 0),
        subRows: [ {
          // TODO: Review this as the Row should not have inside custom row props
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          name: (
            <ul>
              {((Array.isArray(column) && column.includes('login_details')) || column === 'login_details') && (
                <React.Fragment>
                  <li><b>{translate('components.TABLES.LOGIN_DETAILS.USERNAME')}</b> <i>{student.loginDetails?.username}</i></li>
                  <li><b>{translate('components.TABLES.LOGIN_DETAILS.PASSWORD')}</b> <i><StudentsPassword studentId={student.id}/></i></li>
                  <li><b>{translate('components.TABLES.LOGIN_DETAILS.PIN')}</b> <i><StudentsPassword type={'pin'} studentId={student.id}/></i></li>
                </React.Fragment>
              )}
              {(subRowKey && student.lessonsDetails && student.lessonsDetails[subRowKey as keyof StudentType] && ((Array.isArray(column) && column.includes('lesson_details')) || column === 'lesson_details')) && (
                <React.Fragment>
                  <li><b>{translate('components.TABLES.LESSON_DETAILS.TOPIC')}</b> <i>{student.lessonsDetails[subRowKey].topic}</i></li>
                  <li><b>{translate('components.TABLES.LESSON_DETAILS.LESSON')}</b> <i>{student.lessonsDetails[subRowKey].lesson}</i></li>
                  <div className={'md:hidden'}>
                    <Button
                      icon={GLOBAL_ICONS.play}
                      isOutline
                      onClick={showNotSupportedModal}
                    >{translate('components.TABLES.LESSON_DETAILS.PLAY_LESSON')}</Button>
                  </div>
                  <div className={'hidden md:block'}>
                    <Button
                      icon={GLOBAL_ICONS.play}
                      isOutline
                      onClick={playLesson(student.lessonsDetails[subRowKey].lessonId)}
                    >{translate('components.TABLES.LESSON_DETAILS.PLAY_LESSON')}</Button>
                  </div>
                </React.Fragment>
              )}
            </ul>
          ),
        } ],
      });
    });
    return arr;
  };

  return (
    <>
      {modal}
      <StudentsTableContainerStyled ref={ref} $withBorder={withBorder} $color={color} {...other as HTMLProps}>
        <GlobalStyle />
        {headerMessage && (
          <div className={'italic text-muted leading-normal whitespace-pre-wrap mb-md'}>
            {headerMessage}
          </div>
        )}
        <Table
          id={id}
          cols={getColumnsToShow()}
          content={getFormattedData(state)}
          initialState={{
            sortBy: [
              {
                id: sortBy.column,
                desc: sortBy.desc,
              },
            ],
          }}
          noDataMessage={translate('components.TABLES.NO_DATA.STUDENTS')}
          color={color}
        />
        {footerMessage && (
          <div className={'italic text-muted leading-normal mt-md'}>
            {footerMessage}
          </div>
        )}
      </StudentsTableContainerStyled>
    </>
  );
});

const StudentsTableContainerStyled = styled('div')<{ $withBorder: boolean, $color: AllColorType }>`
  ${(props) => props.$withBorder && `
    border: 2px solid ${getPolishedColors(props).main.color};
    border-radius: ${props.theme.radius.default};    
  `}
`;

StudentsTable.displayName = 'StudentsTable';

export default StudentsTable;
